import React, { useState } from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import {
  Box,
  Switch,
  FormControlLabel,
  Paper,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import { Warning as WarningIcon } from "@mui/icons-material";
import SocialConfigForm from "../components/SocialConfigForm/SocialConfigForm";
import BatchForm from "../components/BatchConfigForm";
import BatchConfigV2 from "../components/BatchConfigV2";
import ManagePage from "../components/ManageConfigForm";

const WizardPage = () => {
  const [activeTab, setActiveTab] = useState("social");
  const [isAdvancedMode, setIsAdvancedMode] = useState(false);
  const [hasUploadedFiles, setHasUploadedFiles] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    type: "",
    pendingAction: null,
    currentTab: null,
    currentMode: null,
  });

  // Helper function to check if files have been uploaded
  const hasUnsavedChanges = () => {
    // Only show warning if user has uploaded files and is on batch tab
    return activeTab === "batch" && hasUploadedFiles;
  };

  // Handler to track file uploads
  const handleFileUpload = (hasFiles) => {
    setHasUploadedFiles(hasFiles);
  };

  // Reset file upload state when switching away from batch tab
  const handleTabChangeInternal = (newTab) => {
    if (newTab !== "batch") {
      setHasUploadedFiles(false);
    }
    setActiveTab(newTab);
  };

  // Reset file upload state when switching modes
  const handleModeChangeInternal = (newMode) => {
    setHasUploadedFiles(false);
    setIsAdvancedMode(newMode);
  };

  const handleTabChange = (event, newValue) => {
    if (hasUnsavedChanges() && newValue !== activeTab) {
      setConfirmDialog({
        open: true,
        type: "tab",
        pendingAction: () => handleTabChangeInternal(newValue),
        currentTab: activeTab,
        currentMode: isAdvancedMode,
      });
    } else {
      handleTabChangeInternal(newValue);
    }
  };

  const handleAdvancedModeChange = (event) => {
    const newMode = event.target.checked;
    if (hasUnsavedChanges()) {
      setConfirmDialog({
        open: true,
        type: "mode",
        pendingAction: () => handleModeChangeInternal(newMode),
        currentTab: activeTab,
        currentMode: isAdvancedMode,
      });
    } else {
      handleModeChangeInternal(newMode);
    }
  };

  const handleConfirmDialogClose = () => {
    setConfirmDialog({
      open: false,
      type: "",
      pendingAction: null,
      currentTab: null,
      currentMode: null,
    });
  };

  const handleConfirmProceed = () => {
    if (confirmDialog.pendingAction) {
      confirmDialog.pendingAction();
    }
    handleConfirmDialogClose();
  };

  const getDialogContent = () => {
    if (confirmDialog.type === "tab") {
      return {
        title: "Switch Tab - Unsaved Changes",
        content:
          "You have unsaved changes in your current configuration. Switching tabs will lose all your current progress. Are you sure you want to continue?",
      };
    }
    if (confirmDialog.type === "mode") {
      const modeText = confirmDialog.currentMode ? "Standard" : "Advanced (V2)";
      return {
        title: `Switch to ${modeText} Mode - Unsaved Changes`,
        content: `You have unsaved changes in your current configuration. Switching to ${modeText} mode will lose all your current progress. Are you sure you want to continue?`,
      };
    }
    return { title: "", content: "" };
  };

  return (
    <Box
      sx={{ background: "#f5f5f5", height: "100%" }}
      className="animate__animated animate__fadeIn animate__delay-05s"
    >
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        aria-label="configuration tabs"
        variant="fullWidth"
        centered
        sx={{ m: 2 }}
      >
        <Tab
          value="social"
          label={
            <>
              <strong>Social Media Partners</strong>
              <Typography variant="subtitle2" textTransform="initial">
                Configure leads forms to ensure data is processed to Prospect Services
              </Typography>
            </>
          }
        />
        <Tab
          value="batch"
          label={
            <>
              <strong>CSV Ingestions</strong>
              <Typography variant="subtitle2" textTransform="initial">
                Upload external partner files into Prospect Services
              </Typography>
            </>
          }
        />
        {/* <Tab
          value="manage"
          label={
            <>
              <strong>Manage</strong>
              <Typography variant="subtitle2" textTransform="initial">
                Manage all configurations you have uploaded in the past
              </Typography>
            </>
          }
        /> */}
      </Tabs>

      <Box>
        {activeTab === "social" && <SocialConfigForm />}
        {activeTab === "batch" && (
          <Box>
            <Paper elevation={1} sx={{ m: 2, p: 2, mb: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Configuration Mode
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {isAdvancedMode
                      ? "Advanced mode with enhanced features and modular configuration tools"
                      : "Standard mode with simplified configuration workflow"}
                  </Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isAdvancedMode}
                      onChange={handleAdvancedModeChange}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {isAdvancedMode ? "Advanced (V2)" : "Standard"}
                    </Typography>
                  }
                  labelPlacement="start"
                />
              </Box>
            </Paper>
            <Divider sx={{ mx: 2 }} />
            {isAdvancedMode ? (
              <BatchConfigV2 onFileUpload={handleFileUpload} />
            ) : (
              <BatchForm onFileUpload={handleFileUpload} />
            )}
          </Box>
        )}
        {activeTab === "manage" && <ManagePage />}
      </Box>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={handleConfirmDialogClose}
        aria-labelledby="confirm-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          id="confirm-dialog-title"
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          <WarningIcon color="warning" />
          {getDialogContent().title}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>{getDialogContent().content}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmDialogClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmProceed} color="warning" variant="contained" autoFocus>
            Continue & Lose Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WizardPage;
